import { useEffect, useCallback } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { useRecordNavigation } from "@/hooks/use-record-navigation"

interface RecordNavigationProps {
  /** Entity type key matching API paths: "companies", "contacts", "deals", "tasks", "service_tickets" */
  entityType: string
  /** Current record ID */
  currentId: string | undefined
  /** Base URL path for navigation, e.g. "/companies" or "/service" */
  basePath: string
}

/**
 * Shows prev/next navigation arrows with a position indicator (e.g. "5 z 31").
 * Reads sort/filter context from URL search params and passes them to the
 * neighbors API to maintain list context.
 *
 * Supports keyboard shortcuts: Left/Right arrow keys.
 */
export function RecordNavigation({ entityType, currentId, basePath }: RecordNavigationProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { prevId, nextId, position, total, isLoading } = useRecordNavigation({
    entityType,
    id: currentId,
  })

  // Preserve current search params when navigating
  const navigateTo = useCallback(
    (id: string) => {
      const qs = searchParams.toString()
      navigate(`${basePath}/${id}${qs ? `?${qs}` : ""}`)
    },
    [navigate, basePath, searchParams]
  )

  const handlePrev = useCallback(() => {
    if (prevId) navigateTo(prevId)
  }, [prevId, navigateTo])

  const handleNext = useCallback(() => {
    if (nextId) navigateTo(nextId)
  }, [nextId, navigateTo])

  // Keyboard shortcuts: Left/Right arrow keys
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      // Don't trigger when typing in input/textarea/select or contenteditable
      const target = e.target as HTMLElement
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.tagName === "SELECT" ||
        target.isContentEditable
      ) {
        return
      }

      if (e.key === "ArrowLeft" && prevId) {
        e.preventDefault()
        handlePrev()
      } else if (e.key === "ArrowRight" && nextId) {
        e.preventDefault()
        handleNext()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [prevId, nextId, handlePrev, handleNext])

  // Don't render anything while loading or if no position data
  if (isLoading || position === null || total === null) {
    return null
  }

  return (
    <div className="inline-flex items-center gap-1">
      <button
        onClick={handlePrev}
        disabled={!prevId}
        title="Poprzedni rekord (strzalka w lewo)"
        className="rounded-md p-1 transition-colors hover:bg-muted/30 text-muted-foreground disabled:opacity-30 disabled:cursor-default disabled:hover:bg-transparent"
      >
        <ChevronLeft size={16} />
      </button>
      <span className="min-w-[3rem] text-center text-xs tabular-nums text-muted-foreground select-none">
        {position} z {total}
      </span>
      <button
        onClick={handleNext}
        disabled={!nextId}
        title="Nastepny rekord (strzalka w prawo)"
        className="rounded-md p-1 transition-colors hover:bg-muted/30 text-muted-foreground disabled:opacity-30 disabled:cursor-default disabled:hover:bg-transparent"
      >
        <ChevronRight size={16} />
      </button>
    </div>
  )
}
